<template>
  <!-- Mobile layout -->
  <div v-if="variant === 'mobile'" class="w-full flex flex-col items-center space-y-6 pt-6 px-4">
    <!-- 1. Trip Type -->
    <div class="w-full">
      <h3 class="text-lg font-normal text-center mb-6">Select trip style</h3>
      <div class="grid grid-cols-4 gap-[10px]">
        <button
          v-for="tripType in tripTypeOptionsPlan"
          :key="tripType.id"
          @click="toggleTripType(tripType.id)"
          :class="[
            'flex flex-col items-center justify-center p-2 rounded-lg transition-all w-full aspect-square border',
            selectedTripTypes.includes(tripType.id)
              ? 'border-gray-200 bg-gray-100'
              : 'border-gray-200 bg-white hover:bg-gray-50'
          ]"
        >
          <component :is="tripType.icon" class="w-6 h-6 mb-1" stroke-width="1.5" />
          <span class="text-xs font-semibold text-center leading-tight">{{ tripType.label }}</span>
        </button>
      </div>
    </div>

    <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4"></div>

    <!-- 2. Activities -->
    <div class="w-full">
      <div class="relative flex items-center justify-center mb-6">
        <h3 class="text-lg font-normal text-center">Add preferred activities</h3>
        <button
          v-if="selectedActivities.length > 0"
          @click="clearActivities"
          class="absolute right-0 text-sm text-[#2F3033] hover:text-[#2F3033] underline transition-colors"
        >
          Clear
        </button>
      </div>
      <div class="w-full">
        <TripfinderKeywordTaginput
          v-model="selectedActivities"
          :max-tags="15"
          hide-clear-all
        />
      </div>
    </div>

    <div class="w-[calc(100%+32px)] h-[1px] bg-gray-200 -ml-4"></div>

    <!-- 3. Budget + Travellers + Direction -->
    <div class="w-full pb-4">
      <h3 class="text-lg font-normal text-center mb-6">Set trip budget</h3>
      <div class="bg-[#e0f7ef]/40 p-5 rounded-[32px] flex flex-col gap-4">
        <div class="w-full">
          <TripFinderBudgetForm v-model="budgetAmount" />
        </div>
        <div class="w-full">
          <Travellers
            ref="travellersRef"
            v-model="travelersData"
            :is-mobile="false"
            :inline-dropdown="true"
            :compact="true"
            @dropdown-open="travellersDropdownOpen = true"
            @dropdown-close="travellersDropdownOpen = false"
            :show-error="needsTravellerValidation"
          />
        </div>
        <!-- Return / One way -->
        <div class="relative flex flex-row rounded-full border border-[#2F3033] bg-white shadow-sm transition-all duration-300 w-full items-center isolate">
          <div
            class="absolute tripfinder-toggle-slider -top-[1px] -bottom-[1px] w-1/2 bg-[#2F3033] rounded-full shadow-sm transition-all duration-300 z-0 border border-gray-900/10"
            :class="tripDirection === 'return' ? 'left-0' : 'left-1/2'"
          ></div>
          <button
            @click="tripDirection = 'return'"
            class="relative z-10 flex-1 flex items-center justify-center px-0 py-[14px] text-lg font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center"
            :class="tripDirection === 'return' ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]'"
          >
            Return
          </button>
          <button
            @click="tripDirection = 'oneway'"
            class="relative z-10 flex-1 flex items-center justify-center px-0 py-[14px] text-lg font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center"
            :class="tripDirection === 'oneway' ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]'"
          >
            One way
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Desktop layout -->
  <div v-else class="grid grid-cols-1 md:grid-cols-12 gap-[20px] px-4">
    <!-- Column 1: Trip Type -->
    <div class="relative space-y-6 md:col-span-4">
      <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
      <div>
        <h3 class="text-base font-semibold text-center mb-6 md:mt-6">Select trip style</h3>
        <div class="grid grid-cols-3 gap-[10px] px-0">
          <button
            v-for="tripType in tripTypeOptionsPlan"
            :key="tripType.id"
            @click="toggleTripType(tripType.id)"
            :class="[
              'flex flex-col items-center justify-center p-1 rounded-lg border-[1px] transition-all w-full aspect-square',
              selectedTripTypes.includes(tripType.id)
                ? 'border-gray-200 bg-gray-100'
                : 'border-gray-200 bg-white hover:bg-gray-50'
            ]"
          >
            <component :is="tripType.icon" class="w-6 h-6 mb-1" stroke-width="1.5" />
            <span class="text-xs font-semibold text-center leading-tight">{{ tripType.label }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Column 2: Activities -->
    <div class="relative space-y-6 md:col-span-4">
      <div class="hidden md:block absolute -right-[10px] top-[15%] h-[70%] w-[1px] bg-gray-200"></div>
      <div>
        <div class="relative flex items-center justify-center mb-6">
          <h3 class="text-base font-semibold md:mt-1 text-center">Add preferred activities</h3>
          <button
            v-if="selectedActivities.length > 0"
            @click="clearActivities"
            class="absolute right-4 text-sm text-[#2F3033] hover:text-[#2F3033] underline transition-colors md:hidden"
          >
            Clear selected activities
          </button>
        </div>
        <div class="w-full relative">
          <TripfinderKeywordTaginput
            v-model="selectedActivities"
            :max-tags="15"
            desktop-style
            hide-clear-all
          />
          <div class="flex justify-end mt-2">
            <button
              v-if="selectedActivities.length > 0"
              @click="clearActivities"
              class="hidden md:block text-sm text-gray-500 hover:text-gray-700 underline transition-colors"
            >
              Clear activities
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Column 3: Budget + Travellers + Direction -->
    <div class="space-y-6 md:col-span-4">
      <div class="mb-12">
        <h3 class="text-base font-semibold text-center mb-8 md:mt-6">Set trip budget</h3>
        <div class="bg-[#e0f7ef]/40 p-5 rounded-[32px] flex flex-col gap-4">
          <div class="w-full text-base">
            <TripFinderBudgetForm v-model="budgetAmount" />
          </div>
          <div class="w-full">
            <Travellers
              ref="travellersRef"
              v-model="travelersData"
              :is-mobile="false"
              :inline-dropdown="true"
              :compact="true"
              @dropdown-open="travellersDropdownOpen = true"
              @dropdown-close="travellersDropdownOpen = false"
              :show-error="needsTravellerValidation"
            />
          </div>
          <!-- Return / One way -->
          <div class="relative flex flex-row rounded-full border border-[#2F3033] bg-white shadow-sm transition-all duration-300 w-full items-center isolate h-[44px]">
            <div
              class="absolute tripfinder-toggle-slider -top-[1px] -bottom-[1px] w-1/2 bg-[#2F3033] rounded-full shadow-sm transition-all duration-300 z-0 border border-gray-900/10"
              :class="tripDirection === 'return' ? 'left-0' : 'left-1/2'"
            ></div>
            <button
              @click="tripDirection = 'return'"
              class="relative z-10 flex-1 flex items-center justify-center py-2 px-4 text-base font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center"
              :class="tripDirection === 'return' ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]'"
            >
              Return
            </button>
            <button
              @click="tripDirection = 'oneway'"
              class="relative z-10 flex-1 flex items-center justify-center py-2 px-4 text-base font-semibold rounded-full transition-all duration-200 focus:outline-none whitespace-nowrap leading-tight text-center"
              :class="tripDirection === 'oneway' ? 'text-white' : 'text-[#2F3033] hover:text-[#2F3033]'"
            >
              One way
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRefineStore } from '@/store/refine.js'
import { tripTypeOptionsPlan } from '@/config/tripFinderOptions.js'
import TripFinderBudgetForm from './TripFinderBudgetForm.vue'
import TripfinderKeywordTaginput from './TripfinderKeywordTaginput.vue'
import Travellers from './Travellers.vue'

defineOptions({ name: 'RefineContentPlan' })

const props = defineProps({
  variant: { type: String, default: 'mobile' } // 'mobile' | 'desktop'
})

const refine = useRefineStore()
const travellersRef = ref(null)
const travellersDropdownOpen = ref(false)

const selectedTripTypes = computed(() => refine.selectedTripTypes)
const selectedActivities = computed({
  get: () => refine.selectedActivities,
  set: (val) => refine.selectedActivities = val
})
const budgetAmount = computed({
  get: () => refine.budgetAmount,
  set: (val) => refine.budgetAmount = val
})
const travelersData = computed({
  get: () => refine.travelersData,
  set: (val) => refine.travelersData = val
})
const tripDirection = computed({
  get: () => refine.tripDirection || 'return',
  set: (val) => refine.tripDirection = val
})

const hasBudget = computed(() => refine.budgetAmount !== '' && refine.budgetAmount > 0)
const hasTravellers = computed(() => refine.travelersData.adults > 0 || refine.travelersData.children > 0)
const needsTravellerValidation = computed(() => hasBudget.value && !hasTravellers.value)

const toggleTripType = (typeId) => {
  const current = refine.selectedTripTypes
  refine.selectedTripTypes = current.includes(typeId)
    ? current.filter(id => id !== typeId)
    : [...current, typeId]
}

const clearActivities = () => { refine.selectedActivities = [] }

const handleClickOutside = (event) => {
  if (travellersDropdownOpen.value && travellersRef.value) {
    const el = travellersRef.value.$el
    if (el && !el.contains(event.target)) {
      if (typeof travellersRef.value.closeDropdown === 'function') {
        travellersRef.value.closeDropdown()
      }
    }
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>
